import React, {Component} from "react"
import PropTypes from "prop-types"
import {Row, Col} from "react-bootstrap"
import moment from "moment"
import {find, findIndex, isNil, omitBy, isArray, isEqual, isNaN, parseInt} from "lodash"
import styled from "styled-components"

import id from "@irati/utils/id"
import Select from "@irati/ui/src/FormItem/Select"
import padding from "@irati/ui/src/global/padding"
import {injectIntl, getMessage} from "@irati/utils/lang"
import {mapTeamsToOptions, mapDriversToOptions} from "@irati/insights/utils/mapping"

const TIME_OPTIONS = [
  {label: "insights.filters.time.values.allTime", value: null},
  {
    label: "insights.filters.time.values.lastMonth",
    value: moment()
      .utc()
      .subtract(1, "month")
      .format()
  },
  {
    label: "insights.filters.time.values.lastThreeMonths",
    value: moment()
      .utc()
      .subtract(3, "months")
      .format()
  },
  {
    label: "insights.filters.time.values.lastSixMonths",
    value: moment()
      .utc()
      .subtract(6, "months")
      .format()
  }
]

const FilterRow = styled(Row)`
  width: calc(100% + 60px);
  margin-left: -30px;
  margin-bottom: ${padding.small};
`

class ListFilter extends Component {
  static propTypes = {
    onChange: PropTypes.func,
    hasTime: PropTypes.bool,
    formatMessage: PropTypes.func.isRequired,
    drivers: PropTypes.array,
    teams: PropTypes.array,
    values: PropTypes.object
  }
  static defaultProps = {
    onChange: null,
    hasTime: false,
    drivers: null,
    teams: null,
    values: null
  }

  constructor(props) {
    super(props)

    const driverOptions = this.mapDriversToOptions(props.drivers)
    const teamOptions = this.mapTeamsToOptions(props.teams)
    const timeOptions = TIME_OPTIONS.map(this.intlOption)

    this.state = {
      driverOptions,
      teamOptions,
      timeOptions
    }

    const values = this.getSyncedValues()

    this.state = {...this.state, ...values}
  }

  componentDidUpdate(prevProps) {
    const {values: prevPropValues} = prevProps
    const {values: propValues} = this.props

    if (isEqual(prevPropValues, propValues)) return

    const stateValues = this.getStateValuesAsPropValues(this.state)
    if (propValues.timeId) propValues.timeId = parseInt(propValues.timeId)

    /* eslint-disable react/no-did-update-set-state */
    /** disabled this lint because the setState is wrapped in a conditional */
    if (!isEqual(stateValues, propValues)) {
      const newStateValues = this.getSyncedValues()
      const newPropValues = this.getStateValuesAsPropValues(newStateValues)
      const doPropsRequireTweaking = !isEqual(newPropValues, propValues)
      this.setState({...newStateValues}, doPropsRequireTweaking ? this.emitFilters : undefined)
    }
    /* eslint-enable react/no-did-update-set-state */
  }

  componentDidMount() {
    this.emitFilters()
  }

  useDriverFilter = () => !!this.props.drivers
  useTimeFilter = () => this.props.hasTime
  useTeamFilter = () => !!this.props.teams

  getStateValuesAsPropValues = (stateValues) => {
    const {
      timeId,
      timeValue: {value: time},
      teamValue: {value: teamId},
      driverValue: {value: driverId}
    } = stateValues
    const propValues = {
      timeId,
      time,
      teamId,
      driverId
    }

    return omitBy(propValues, isNil)
  }

  getSyncedValues = () => {
    const {values} = this.props
    const {driverId, teamId, timeId} = values
    const {driverOptions, teamOptions, timeOptions} = this.state

    const driverValue = find(driverOptions, {value: driverId}) || driverOptions[0]
    const teamValue = find(teamOptions, {value: teamId}) || teamOptions[0]

    /** short term filtering by time index */
    const defaultTimeIndex = 2
    let timeIndex = parseInt(timeId)
    if (isNaN(timeIndex)) timeIndex = defaultTimeIndex
    if (timeIndex >= timeOptions.length) timeIndex = defaultTimeIndex
    const timeValue = timeOptions[timeIndex]

    return {driverValue, teamValue, timeValue, timeId: timeIndex}
  }

  handleChange = (stateValue, options, value) => {
    const newState = {[stateValue]: find(options, {value})}

    if (stateValue === "timeValue") {
      newState.timeId = findIndex(options, {value})
    }

    this.setState(newState, this.emitFilters)
  }

  emitFilters = () => {
    const {onChange} = this.props
    const {
      driverValue: {value: driverId},
      teamValue: {value: teamId},
      timeValue: {value: time},
      timeId
    } = this.state

    const emittedFilters = omitBy({time, timeId, driverId, teamId}, isNil)

    if (onChange) onChange(emittedFilters)
  }

  intlOption = ({label, value} = {}) => {
    return {label: getMessage(this, label), value}
  }

  mapDriversToOptions = (drivers) => {
    let driverOptions = []

    if (isArray(drivers)) driverOptions = mapDriversToOptions(drivers, this)
    driverOptions.unshift(this.intlOption({value: null, label: "insights.filters.driver.values.allDrivers"}))

    return driverOptions
  }

  mapTeamsToOptions = (teams) => {
    let teamOptions = []

    if (isArray(teams)) teamOptions = mapTeamsToOptions(teams)
    teamOptions.unshift(this.intlOption({value: null, label: "insights.filters.team.values.allTeams"}))

    return teamOptions
  }

  render() {
    const idNamespace = id("ListFilter", this.props)
    const {formatMessage} = this.props
    const {driverOptions, driverValue, teamOptions, teamValue, timeOptions, timeValue} = this.state

    return (
      <FilterRow id={idNamespace}>
        {this.useTimeFilter() && (
          <Col xs={12} md={2}>
            <Select
              id={id("Time", idNamespace)}
              name="period"
              label={formatMessage({id: "insights.filters.time.label"})}
              options={timeOptions}
              value={timeValue}
              isClearable={false}
              onChange={(value) => this.handleChange("timeValue", timeOptions, value)} />
          </Col>
        )}

        {this.useTeamFilter() && (
          <Col xs={12} md={2}>
            <Select
              id={id("Team", idNamespace)}
              name="team"
              label={formatMessage({id: "insights.filters.team.label"})}
              options={teamOptions}
              value={teamValue}
              isClearable={false}
              onChange={(value) => this.handleChange("teamValue", teamOptions, value)} />
          </Col>
        )}

        {this.useDriverFilter() && (
          <Col xs={12} md={2}>
            <Select
              id={id("Driver", idNamespace)}
              name="driver"
              label={formatMessage({id: "insights.filters.driver.label"})}
              value={driverValue}
              options={driverOptions}
              isClearable={false}
              onChange={(value) => this.handleChange("driverValue", driverOptions, value)} />
          </Col>
        )}
      </FilterRow>
    )
  }
}

export default injectIntl(ListFilter)
